import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppDispatch, RootState } from "configureStore";
import { deleteOrderDelivery } from "../deliveries.slice";
import { Delivery } from "types/Delivery";

/**
 * Who may edit / delete one delivery, plus the delete flow itself. Shared by
 * the delivery card and the deliveries table so both apply the same rules.
 */
export function useDeliveryActions(delivery: Delivery, onRefresh: () => void) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useParams<{ companyId: string }>();
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if user is superadmin (Master)
  const { role } = useSelector((state: RootState) => state.authReducer);
  const isSuperAdmin = role?.permissionsgroups?.includes("Master");

  // Deliveries synced from terminalas are read-only in the ERP UI — the
  // backend rejects edits/deletes on them (DELIVERY_MANAGED_BY_TERMINALAS),
  // even for Master, so the controls are hidden here rather than shown and
  // failing.
  const isTerminalas = delivery.source === "terminalas";
  const canEdit =
    !isTerminalas &&
    (isSuperAdmin || delivery.status === "draft" || delivery.status === "planned");
  const canDelete = !isTerminalas && (isSuperAdmin || delivery.status === "draft");

  const handleDelete = async () => {
    if (!companyId) return;

    const confirmed = window.confirm(t("deliveries_delete_confirm"));
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteOrderDelivery(companyId, delivery.id));
      onRefresh();
    } catch (error) {
      console.error("Failed to delete delivery:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return { isTerminalas, canEdit, canDelete, isDeleting, handleDelete };
}
